// RealtimeInvalidationMap — канон: Socket.IO terminal event → TanStack Query keys.
//
// Потребитель: RealtimeInvalidationProvider (__root). Detail-роуты могут
// дополнительно делать optimistic setQueryData / navigate / toast, но списки
// и панели обновляются через этот registry — не через detail-only подписки.
//
// Правила (.claude/rules/realtime.md §6.2 / §12, frontend-realtime.md §1):
//   • каждая ready-пара имеет failed-entry (TERMINAL_EVENT_PAIRS);
//   • PowerSync-primary данные всё равно инвалидируем как hint (tRPC fallback /
//     несинкаемые списки);
//   • notification:new НЕ входит — это toast-канал, не data.

import type { QueryKey } from "@tanstack/react-query";

export type InvalidationEntry = {
	readonly event: string;
	readonly keys: readonly QueryKey[];
	/** При reconnect — тоже инвалидировать (висящие generating flows). */
	readonly onReconnect?: boolean;
};

// ВАЖНО про состав ключей: `invalidateQueries` матчит по ПРЕФИКСУ, поэтому
// вложенный ключ рядом со своим корнем — чистый дубль работы. `["notes"]` уже
// накрывает `["notes","list"]` и `["notes","list","material",id]`; `["materials"]`
// — `["materials","trash","list"]`. Такие подключи убраны (реестр §3c R8/R10),
// а разные корни (`["material"]` vs `["materials"]`) остаются: это РАЗНЫЕ ключи,
// не префиксы друг друга.
const notesKeys = [["notes"]] as const;
const flashcardsKeys = [
	["flashcards"],
	["flashcard-sets"],
	["flashcard-set"],
	["flashcardSets"],
	["material"],
] as const;
const quizKeys = [["quizzes"], ["material"]] as const;
const weakSpotsKeys = [["weakSpots"], ["weak-spots"]] as const;
const glossaryKeys = [["glossary"]] as const;
const studyPlanKeys = [["studyPlan"]] as const;
/** Курсы (обучающая система): список курсов, деталь курса, экран темы. */
const coursesKeys = [["courses"]] as const;
/**
 * Шаблоны и каталог — ОТДЕЛЬНЫЙ неймспейс tRPC (`api.courseTemplates`).
 *
 * Ключ `["courses"]` его НЕ покрывает, хотя оба про курсы: инвалидация идёт по
 * префиксу пути процедуры, а состояние публикации живёт в
 * `courseTemplates.getForCourse` (пилюля «на проверке» в шапке курса),
 * `courseTemplates.listMine` («Мои шаблоны») и `courseTemplates.catalogList`.
 * Без этого ключа исход проверки не снимает «на проверке» до перезагрузки.
 */
const courseTemplatesKeys = [["courseTemplates"]] as const;
const tutorKeys = [["tutor"]] as const;
const discoveryKeys = [["discovery"]] as const;
/**
 * Прогресс: `progress.getCourseMastery` (виджет «Освоение курса» в шапке
 * воркспейса) и остальные проекции прогресса. Артефакт темы, готовый или
 * закрытый, меняет освоение — без этого ключа кольцо в шапке остаётся вчерашним
 * до перезагрузки.
 */
const progressKeys = [["progress"]] as const;
/**
 * Материалы + Библиотека. `library.*` — tRPC-агрегат (фильтры «не в курсе» /
 * «без папки»), не PowerSync: без этого ключа карточка появляется только после F5.
 */
const materialsKeys = [["materials"], ["material"], ["library"], ["trash"]] as const;
const audioKeys = [["audioRecaps"], ["audio-recaps"]] as const;
const essayKeys = [["essays"]] as const;
const visualKeys = [["visual"], ["visuals"], ["visualExplainer"]] as const;
const setsKeys = [["folders"]] as const;
const voiceKeys = [["voiceTutor"], ["voice-tutor"]] as const;
const recommendationsKeys = [["recommendations"]] as const;
const streaksKeys = [["streaks"]] as const;
const recordingsKeys = [["recordings"]] as const;
const calendarKeys = [["calendar"], ["studyPlan"]] as const;
const tutorSessionsKeys = [["tutor", "sessions"]] as const;
/**
 * Карта доступности модулей. Ключ корневой (`["modules"]`), потому что
 * инвалидация матчит по префиксу, а процедура одна — `modules.availability`.
 */
const moduleAvailabilityKeys = [["modules"]] as const;
/**
 * Что перечитать при смене тарифа.
 *
 * Три ключа, и каждый нужен: `auth` несёт сам тариф в сессии, `quotas` — лимиты,
 * которые от тарифа зависят, `billing` — экран подписки. Раньше при оплате
 * инвалидировался только третий, поэтому после апгрейда квоты на фронте
 * показывали старые лимиты, а бейдж — старый уровень.
 *
 * `modules` — потому что `minTier` манифестов решает видимость разделов:
 * без него купленный Pro не открывал бы разделы до перезагрузки.
 *
 * `iecId` — зеркало прав экосистемы (`iecId.entitlements.get`): на нём держатся
 * экран подписки, баннер пробного и правило «кнопку покупки не показываем».
 * Без него право приезжало бы событием, а поверхности читали вчерашний снимок.
 */
const subscriptionKeys = [["auth"], ["quotas"], ["billing"], ["modules"], ["iecId"]] as const;

/** Все terminal / list-invalidation события → keys. */
export const REALTIME_INVALIDATION_MAP: readonly InvalidationEntry[] = [
	// Артефакты ниже — это ещё и активности темы курса. Без ["courses"] готовый
	// конспект/набор/тест обновлял бы только «свой» раздел, а маршрут курса,
	// сцена темы и счётчики шапки воркспейса оставались бы вчерашними
	// (правило wire-up №14: realtime во ВСЕ поверхности).
	// ─── Notes ───
	{ event: "note:ready", keys: [...notesKeys, ...coursesKeys], onReconnect: true },
	{ event: "note:generation_failed", keys: [...notesKeys, ...coursesKeys], onReconnect: true },
	// ─── Flashcards ───
	{
		event: "flashcards:ready",
		keys: [...flashcardsKeys, ...coursesKeys, ...progressKeys],
		onReconnect: true,
	},
	{
		event: "flashcards:generation_failed",
		keys: [...flashcardsKeys, ...coursesKeys],
		onReconnect: true,
	},
	// ─── Quiz ───
	{ event: "quiz:ready", keys: [...quizKeys, ...coursesKeys, ...progressKeys], onReconnect: true },
	{ event: "quiz:generation_failed", keys: [...quizKeys, ...coursesKeys], onReconnect: true },
	// ─── Glossary ───
	{ event: "glossary:ready", keys: [...glossaryKeys, ...coursesKeys], onReconnect: true },
	{ event: "glossary:failed", keys: [...glossaryKeys, ...coursesKeys], onReconnect: true },
	// ─── Audio recap ───
	{ event: "audio-recap:ready", keys: [...audioKeys, ...coursesKeys], onReconnect: true },
	{ event: "audio-recap:script_ready", keys: audioKeys },
	{ event: "audio-recap:title_changed", keys: audioKeys },
	{ event: "audio-recap:failed", keys: [...audioKeys, ...coursesKeys], onReconnect: true },
	// ─── Study plan ───
	{ event: "study-plan:ready", keys: [...studyPlanKeys, ...coursesKeys], onReconnect: true },
	{
		event: "study-plan:generation:failed",
		keys: [...studyPlanKeys, ...coursesKeys],
		onReconnect: true,
	},
	{
		event: "study-plan:progress_updated",
		keys: [...studyPlanKeys, ...coursesKeys, ...progressKeys],
	},
	{
		event: "study-plan:completed",
		keys: [...studyPlanKeys, ...coursesKeys, ...progressKeys, ...recommendationsKeys],
	},
	// Комплект темы наполняет и экраны курса (список / деталь / тема) —
	// без ["courses"] карточка темы обновлялась бы только на detail-роуте.
	{
		event: "study-plan:subsection-materials:ready",
		keys: [...studyPlanKeys, ...coursesKeys],
		onReconnect: true,
	},
	{
		event: "study-plan:subsection-materials:failed",
		keys: [...studyPlanKeys, ...coursesKeys],
		onReconnect: true,
	},
	{ event: "study-plan:materials-linked", keys: studyPlanKeys },
	{ event: "study-plan:calendar.ready", keys: calendarKeys },
	// ─── Курсы / темы (обучающая система) ───
	/**
	 * Состав курса меняется и из других поверхностей — бейдж «в N курсах» и
	 * счётчик «не в курсе» в Библиотеке обязаны обновиться без F5.
	 */
	{
		event: "course:composition_changed",
		keys: [...coursesKeys, ...setsKeys, ...studyPlanKeys, ...materialsKeys],
		onReconnect: true,
	},
	{
		event: "course:topic_kit_ready",
		keys: [...coursesKeys, ...studyPlanKeys],
		onReconnect: true,
	},
	{
		event: "course:topic_kit_failed",
		keys: [...coursesKeys, ...studyPlanKeys],
		onReconnect: true,
	},
	{
		// Освоение курса (кольцо в шапке воркспейса) считается по картам, тестам
		// и завершённым темам — закрытие темы обязано его подвинуть.
		event: "course:topic_completed",
		keys: [...coursesKeys, ...studyPlanKeys, ...recommendationsKeys, ...progressKeys],
		onReconnect: true,
	},
	{
		// Программа пересобрана (мягко или с нуля): маршрут слева, сцена, счётчики
		// шапки и календарь курса обязаны обновиться во ВСЕХ поверхностях, а не
		// только там, где нажали кнопку.
		event: "course:program_updated",
		keys: [...coursesKeys, ...studyPlanKeys, ...progressKeys, ...calendarKeys],
		onReconnect: true,
	},
	{ event: "course:cover_ready", keys: coursesKeys, onReconnect: true },
	{ event: "course:cover_failed", keys: coursesKeys, onReconnect: true },
	{ event: "course:translation_ready", keys: coursesKeys, onReconnect: true },
	{ event: "course:translation_failed", keys: coursesKeys, onReconnect: true },
	{ event: "course:topic_translation_ready", keys: coursesKeys, onReconnect: true },
	{ event: "course:topic_translation_failed", keys: coursesKeys, onReconnect: true },
	// Витринные тексты курса (публичная страница + мастер публикации). Ключи
	// курсов — потому что страница шаблона и карточка каталога читаются теми же
	// запросами; `onReconnect` обязателен: генерация переживает разрыв связи,
	// и без переспроса автор остался бы с «генерируем» на готовом описании.
	{
		event: "course-template:listing_ready",
		keys: [...courseTemplatesKeys, ...coursesKeys],
		onReconnect: true,
	},
	{
		event: "course-template:listing_failed",
		keys: [...courseTemplatesKeys, ...coursesKeys],
		onReconnect: true,
	},
	// Исход автопроверки публикации. Ключи курсов — теми же запросами читаются
	// карточка каталога, страница курса и список «Мои шаблоны», а состояние
	// проверки меняет ВСЕ три поверхности сразу: до одобрения курс виден только
	// автору и с плашкой. `onReconnect` обязателен: проверка длится минуты и
	// переживает разрыв связи — без переспроса автор остался бы с «на проверке»
	// на уже опубликованном курсе.
	{
		event: "course-template:review_done",
		// ОБА неймспейса: `courseTemplates` — пилюля в шапке, «Мои шаблоны» и
		// каталог; `courses` — карточка курса, из которой автор публиковал.
		keys: [...courseTemplatesKeys, ...coursesKeys],
		onReconnect: true,
	},
	// ─── Documents / materials ───
	{ event: "document:ready", keys: materialsKeys, onReconnect: true },
	{ event: "document:failed", keys: materialsKeys, onReconnect: true },
	{ event: "document:cancelled", keys: materialsKeys, onReconnect: true },
	// PowerSync-primary list — всё равно инвалидируем tRPC trash/favorites/detail
	{ event: "materials:list:invalidated", keys: materialsKeys },
	// Импорт веб-источника в курс (успех или thin/paywall): строка состава и
	// статус материала обязаны обновиться без F5. PowerSync двигает candidate/
	// material, tRPC-кэш `courses.get` — отдельно.
	{
		event: "discovery:import_settled",
		keys: [...coursesKeys, ...materialsKeys, ...setsKeys],
		onReconnect: true,
	},
	{
		event: "discovery:ready",
		keys: [...discoveryKeys, ...coursesKeys],
		onReconnect: true,
	},
	{
		event: "discovery:failed",
		keys: [...discoveryKeys, ...coursesKeys],
		onReconnect: true,
	},
	// ─── Voice lesson plan ───
	{ event: "voice:lesson_plan:ready", keys: voiceKeys, onReconnect: true },
	{ event: "voice:lesson_plan:title_changed", keys: voiceKeys },
	{ event: "voice:lesson_plan:failed", keys: voiceKeys, onReconnect: true },
	// ─── Set-wide generation ───
	{ event: "folders:note_ready", keys: [...setsKeys, ...notesKeys], onReconnect: true },
	{ event: "folders:note_failed", keys: [...setsKeys, ...notesKeys], onReconnect: true },
	{ event: "folders:flashcards_ready", keys: [...setsKeys, ...flashcardsKeys], onReconnect: true },
	{ event: "folders:flashcards_failed", keys: [...setsKeys, ...flashcardsKeys], onReconnect: true },
	{ event: "folders:quiz_ready", keys: [...setsKeys, ...quizKeys], onReconnect: true },
	{ event: "folders:quiz_failed", keys: [...setsKeys, ...quizKeys], onReconnect: true },
	{ event: "folders:copy:ready", keys: setsKeys, onReconnect: true },
	{ event: "folders:copy:failed", keys: setsKeys, onReconnect: true },
	{ event: "folders:export_ready", keys: setsKeys },
	{ event: "folders:export_failed", keys: setsKeys },
	{ event: "folders:artifact_copy:ready", keys: setsKeys },
	{ event: "folders:artifact_copy:failed", keys: setsKeys },
	{ event: "folders:deleted", keys: setsKeys },
	{ event: "folders:updated", keys: setsKeys },
	{ event: "folders:shared", keys: setsKeys },
	// ─── Visual / essays / recordings ───
	{ event: "visual:ready", keys: visualKeys, onReconnect: true },
	{ event: "visual:status_changed", keys: visualKeys, onReconnect: true },
	{ event: "visual:categorized", keys: visualKeys },
	{ event: "essays:ready", keys: essayKeys, onReconnect: true },
	{ event: "essays:status_changed", keys: essayKeys, onReconnect: true },
	{ event: "essays:report_ready", keys: essayKeys },
	{ event: "recording:processed", keys: recordingsKeys, onReconnect: true },
	{ event: "recording:title_changed", keys: recordingsKeys },
	{ event: "recording:artifacts_rebuilt", keys: recordingsKeys },
	{ event: "recording:artifacts_rebuild_failed", keys: recordingsKeys },
	{ event: "recording:audio_ready", keys: recordingsKeys },
	// ─── Progress / recommendations ───
	{ event: "recommendations:ready", keys: recommendationsKeys },
	{ event: "streaks:updated", keys: streaksKeys },
	{ event: "streaks:broken", keys: streaksKeys },
	{ event: "streaks:milestone", keys: streaksKeys },
	// ─── Модульная платформа ───
	// Админ переключил раскатку / сработал killswitch / сменился тариф. Одна
	// запись здесь — весь механизм «тумблер влияет без перезагрузки» (метрика М4,
	// ≤60с): инвалидация ключа → рефетч `modules.availability` →
	// `ModuleAvailabilityProvider` перезаписывает офлайн-снапшот, и навигация,
	// Route Guard и панели перечитывают карту из стора.
	// `onReconnect` обязателен: пока клиент был без сети, событие могло пройти
	// мимо, и карта осталась бы вчерашней до следующего изменения.
	{
		event: "modules:availability_changed",
		keys: moduleAvailabilityKeys,
		onReconnect: true,
	},
	// ─── Тариф: локальная подписка ∪ права от IEC ID ───
	// `onReconnect` обязателен: пока вкладка была без сети, право могло истечь
	// или приехать подарком, и уровень остался бы вчерашним до следующего
	// изменения — то есть, возможно, навсегда.
	{ event: "subscription:changed", keys: subscriptionKeys, onReconnect: true },
	// ─── Tutor chat auto-title / rename ───
	{
		event: "tutor:session:title_changed",
		keys: [...tutorSessionsKeys, ...materialsKeys, ...notesKeys],
	},
	{
		event: "tutor:artifact_ready",
		keys: [...tutorKeys, ...coursesKeys],
		onReconnect: true,
	},
	{
		event: "tutor:artifact_failed",
		keys: [...tutorKeys, ...coursesKeys],
		onReconnect: true,
	},
	{
		event: "quiz:free_answer_graded",
		keys: [...quizKeys, ...progressKeys, ...weakSpotsKeys],
	},
	{
		event: "quiz:attempt_graded",
		keys: [...quizKeys, ...progressKeys, ...weakSpotsKeys],
	},
	{
		// Оценка карточки двигает SRS-компоненту освоения курса — без
		// progressKeys пилюля в шапке остаётся вчерашней до staleTime.
		event: "flashcards:rating_applied",
		keys: [...flashcardsKeys, ...progressKeys],
	},
] as const;

/**
 * Пары ready↔failed для gate-теста (realtime §6.2).
 * Events без пары (status_changed, list:invalidated) сюда не входят.
 */
export const TERMINAL_EVENT_PAIRS: readonly {
	readonly ready: string;
	readonly failed: string;
}[] = [
	{ ready: "note:ready", failed: "note:generation_failed" },
	{ ready: "flashcards:ready", failed: "flashcards:generation_failed" },
	{ ready: "quiz:ready", failed: "quiz:generation_failed" },
	{ ready: "glossary:ready", failed: "glossary:failed" },
	{ ready: "course-template:listing_ready", failed: "course-template:listing_failed" },
	{ ready: "audio-recap:ready", failed: "audio-recap:failed" },
	{ ready: "study-plan:ready", failed: "study-plan:generation:failed" },
	{
		ready: "study-plan:subsection-materials:ready",
		failed: "study-plan:subsection-materials:failed",
	},
	{ ready: "course:topic_kit_ready", failed: "course:topic_kit_failed" },
	{ ready: "document:ready", failed: "document:failed" },
	{ ready: "voice:lesson_plan:ready", failed: "voice:lesson_plan:failed" },
	{ ready: "folders:note_ready", failed: "folders:note_failed" },
	{ ready: "folders:flashcards_ready", failed: "folders:flashcards_failed" },
	{ ready: "folders:quiz_ready", failed: "folders:quiz_failed" },
	{ ready: "folders:copy:ready", failed: "folders:copy:failed" },
	{ ready: "folders:export_ready", failed: "folders:export_failed" },
	{ ready: "folders:artifact_copy:ready", failed: "folders:artifact_copy:failed" },
	{ ready: "recording:artifacts_rebuilt", failed: "recording:artifacts_rebuild_failed" },
	{ ready: "course:cover_ready", failed: "course:cover_failed" },
	{ ready: "course:translation_ready", failed: "course:translation_failed" },
	{ ready: "course:topic_translation_ready", failed: "course:topic_translation_failed" },
	{ ready: "discovery:ready", failed: "discovery:failed" },
	{ ready: "tutor:artifact_ready", failed: "tutor:artifact_failed" },
] as const;

/** Keys для invalidate при socket reconnect (висящие generating). */
export function reconnectInvalidationKeys(): QueryKey[] {
	const seen = new Set<string>();
	const out: QueryKey[] = [];
	for (const entry of REALTIME_INVALIDATION_MAP) {
		if (!entry.onReconnect) continue;
		for (const key of entry.keys) {
			const id = JSON.stringify(key);
			if (seen.has(id)) continue;
			seen.add(id);
			out.push([...key]);
		}
	}
	return out;
}

export function keysForEvent(event: string): readonly QueryKey[] {
	const entry = REALTIME_INVALIDATION_MAP.find((e) => e.event === event);
	return entry?.keys ?? [];
}

export function eventsInMap(): ReadonlySet<string> {
	return new Set(REALTIME_INVALIDATION_MAP.map((e) => e.event));
}
